import React, { useState } from "react";
import { Alert, Button, CircularProgress, TextField } from "@mui/material";

/**
 * Inline vendor-cost editor — used on MyAppsOnetimeDetail and
 * DomainDetail (one-time + domain subs), and structurally identical
 * to the section we ship inside AdminUpdateSubscriptionDrawer for
 * recurring subs.
 *
 * Edits ONLY purchasesubscriptions.unit_ids[i].vendor_price. No Stripe
 * call, no schedule update, no pending patch — vendor_price is our
 * internal cost, not the customer billing field.
 *
 * Props:
 *   subscription — the row data ({ id, unit_ids: [...] })
 *   onSaved      — optional callback fired after a successful save
 */
const VendorPriceEditor = ({ subscription, onSaved }) => {
  const [edits, setEdits] = useState({}); // { unit_id: string }
  // Seeding-mode fields — only used when the sub has no unit rows yet.
  const [seedVendor, setSeedVendor] = useState("");
  const [seedQty, setSeedQty] = useState("");
  const [seedMarket, setSeedMarket] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const subId = Number(subscription?.id || 0);
  const unitRows = Array.isArray(subscription?.unit_ids)
    ? subscription.unit_ids
    : [];

  // SEEDING MODE — domain subs (and some one-time ones) are created
  // with an empty unit_ids array, so there was no row to edit and this
  // component rendered nothing at all. Their revenue still reaches the
  // Revenue Tracker, which then books it at 100% margin because there
  // is no vendor cost to subtract. That is a blank, not a profit, and
  // it is one of the things skewing the monthly margin.
  //
  // When there are no unit rows we show a single seed form instead:
  // quantity + what the customer pays + what we pay the vendor. The
  // backend creates the unit row from that on save.
  const needsSeed = unitRows.length > 0 ? false : true;
  const subQty =
    Number(subscription?.noOfLicences) > 0
      ? Number(subscription.noOfLicences)
      : 1;
  const subAmount =
    Number(subscription?.amount_default) ||
    Number(subscription?.amount) ||
    0;
  const derivedMarket = subAmount > 0 ? subAmount / subQty : 0;

  if (!subId) return null;

  const handleSaveSeed = async () => {
    setError("");
    setSuccess("");
    const vendor = Number(seedVendor);
    if (!Number.isFinite(vendor) || vendor < 0) {
      setError("Enter the vendor cost per unit (0 or more).");
      return;
    }
    const qty = Number(seedQty) > 0 ? Number(seedQty) : subQty;
    const market = Number(seedMarket) > 0 ? Number(seedMarket) : derivedMarket;
    if (!market) {
      setError(
        "No marketplace price to compare against. Enter what the customer pays per unit.",
      );
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions/${subId}/vendor_prices`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // unit_id 1 is a placeholder — the backend rewrites it to
            // the id of the row it creates.
            updates: [{ unit_id: 1, vendor_price: vendor }],
            seed_quantity: qty,
            seed_marketplace_price: market,
          }),
        },
      );
      const data = await res.json();
      if (data?.success) {
        setSuccess(
          "Vendor cost saved. A unit row was created for this subscription — it now costs correctly in the Revenue Tracker.",
        );
        if (typeof onSaved === "function") onSaved(data);
      } else {
        setError(data?.msg || "Failed to save vendor cost");
      }
    } catch (err) {
      setError(err?.message || "Failed to save vendor cost");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");
    const updates = Object.entries(edits)
      .filter(([, v]) => v !== "" && v !== undefined && v !== null)
      .map(([unit_id, v]) => ({
        unit_id: Number(unit_id),
        vendor_price: Number(v),
      }))
      .filter(
        (u) =>
          Number.isFinite(u.vendor_price) &&
          u.vendor_price >= 0 &&
          Number.isInteger(u.unit_id),
      );
    if (!updates.length) {
      setError("Edit at least one vendor price before saving.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions/${subId}/vendor_prices`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        },
      );
      const data = await res.json();
      if (data?.success) {
        const notFound = (data.data?.not_found_unit_ids || []).length;
        setSuccess(
          `Updated ${data.data?.updated || 0} unit${(data.data?.updated || 0) === 1 ? "" : "s"}. No Stripe call.${notFound ? ` (${notFound} unit_id${notFound === 1 ? "" : "s"} not found.)` : ""}`,
        );
        setEdits({});
        if (typeof onSaved === "function") onSaved(data);
      } else {
        setError(data?.msg || "Failed to update vendor prices");
      }
    } catch (err) {
      setError(err?.message || "Failed to update vendor prices");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 18,
        marginBottom: 18,
        padding: 14,
        border: "1px dashed #cfd9e6",
        borderRadius: 12,
        background: "#f7fbff",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 700, color: "#1f2c3c", fontSize: 14 }}>
          Vendor cost (internal — no Stripe sync)
        </div>
        <div style={{ color: "#607084", fontSize: 12, marginTop: 2 }}>
          {needsSeed
            ? "This subscription has no priced unit rows yet, so the Revenue Tracker counts it at 100% margin. Add what we pay the vendor to fix that."
            : "Edit what we PAY the vendor per unit. Customer billing (marketplace_price) is untouched."}
        </div>
      </div>
      {needsSeed ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "flex-start",
            borderTop: "1px solid #e6eff7",
            paddingTop: 12,
          }}
        >
          <TextField
            size="small"
            type="number"
            label="Quantity"
            value={seedQty}
            placeholder={String(subQty)}
            onChange={(e) => setSeedQty(e.target.value)}
            inputProps={{ min: 1, step: "1" }}
            sx={{ width: 120 }}
            disabled={saving}
            helperText="Units on this sub"
          />
          <TextField
            size="small"
            type="number"
            label="Customer pays / unit"
            value={seedMarket}
            placeholder={derivedMarket ? derivedMarket.toFixed(2) : "0.00"}
            onChange={(e) => setSeedMarket(e.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
            sx={{ width: 180 }}
            disabled={saving}
            helperText={
              derivedMarket
                ? `Defaults to ${derivedMarket.toFixed(2)} from the sub amount`
                : "Required — no amount on this sub"
            }
          />
          <TextField
            size="small"
            type="number"
            label="We pay vendor / unit"
            value={seedVendor}
            onChange={(e) => setSeedVendor(e.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
            sx={{ width: 180 }}
            disabled={saving}
            helperText="Internal cost — never sent to Stripe"
          />
        </div>
      ) : null}
      {!needsSeed && unitRows.map((u) => {
        const id = Number(u?.id);
        const currentVendor = Number(u?.vendor_price || 0);
        const currentMarket = Number(
          u?.marketplace_price || u?.Marketplace_price || 0,
        );
        const startRange = Number(u?.start_range || u?.StartRange || 0);
        const endRange = Number(u?.end_range || u?.EndRange || 0);
        const editedVal = edits[id] !== undefined ? edits[id] : "";
        return (
          <div
            key={id || Math.random()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 0",
              borderTop: "1px solid #e6eff7",
            }}
          >
            <div
              style={{
                minWidth: 130,
                fontSize: 12.5,
                color: "#3d4a5c",
                fontWeight: 600,
              }}
            >
              Unit #{id}
              <span style={{ color: "#8696aa", fontWeight: 400, marginLeft: 4 }}>
                ({startRange}–{endRange})
              </span>
            </div>
            <div style={{ minWidth: 150, fontSize: 12, color: "#8696aa" }}>
              Mkt: {currentMarket.toFixed(2)} · Current: {currentVendor.toFixed(2)}
            </div>
            <TextField
              size="small"
              type="number"
              placeholder={String(currentVendor)}
              value={editedVal}
              onChange={(e) =>
                setEdits((prev) => ({ ...prev, [id]: e.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              sx={{ flex: 1, maxWidth: 220 }}
              disabled={saving}
            />
          </div>
        );
      })}
      {error && (
        <Alert severity="error" sx={{ mt: 1.25 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 1.25 }}>
          {success}
        </Alert>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={saving ? <CircularProgress size={12} /> : null}
          disabled={
            saving ||
            (needsSeed
              ? seedVendor === "" || seedVendor === null
              : Object.values(edits).every(
                  (v) => v === "" || v === undefined || v === null,
                ))
          }
          onClick={needsSeed ? handleSaveSeed : handleSave}
        >
          {saving
            ? "Saving..."
            : needsSeed
              ? "Add vendor cost"
              : "Save vendor cost"}
        </Button>
      </div>
    </div>
  );
};

export default VendorPriceEditor;
